import {Anchor, Badge, Group, Loader, Paper, Stack, Table, Text, Title} from '@mantine/core'
import {Link} from '@tanstack/react-router'

import {toLifecycleStatus} from 'src/jobs/job-api-adapter'
import {formatDateTbcLabel, formatJobDate} from 'src/jobs/job-schedule-format'
import {jobStatusColor, jobStatusLabel} from 'src/jobs/job-status-badge'

import {PartialPickupBadge} from './partial-pickup'
import type {CustomerWork} from './schemas'
import {useCustomerWork} from './use-work'

function formatEquipmentOutLine(group: CustomerWork['equipmentOut'][number]): string {
    const units = group.unitNames.length ? ` (${group.unitNames.join(', ')})` : ''
    return `${group.quantity} ${group.rentalAssetTypeName}${units}`
}

export function CustomerOpenWorkSection({customerId}: {customerId: string}) {
    const {data: work = [], isLoading, isError} = useCustomerWork(customerId)

    return (
        <Paper withBorder p="md" radius="md" data-testid="customer-open-work">
            <Stack gap="md">
                <Title order={3}>Open orders</Title>
                {isLoading ? (
                    <Loader size="sm" aria-label="Loading open orders" />
                ) : isError ? (
                    <Text size="sm" c="red">
                        Could not load orders
                    </Text>
                ) : work.length === 0 ? (
                    <Text size="sm" c="dimmed">
                        No open orders
                    </Text>
                ) : (
                    work.map((item) => (
                        <Stack key={item.projectId} gap="xs">
                            <Group gap="xs" wrap="wrap">
                                <Anchor
                                    fw={600}
                                    renderRoot={(props) => (
                                        <Link
                                            {...props}
                                            to="/projects/$projectId"
                                            params={{projectId: item.projectId}}
                                        />
                                    )}
                                >
                                    {item.name}
                                </Anchor>
                                {item.orderNumber ? (
                                    <Text size="sm" c="dimmed">
                                        Order {item.orderNumber}
                                    </Text>
                                ) : null}
                                {item.repName ? (
                                    <Text size="sm" c="dimmed">
                                        · {item.repName}
                                    </Text>
                                ) : null}
                            </Group>
                            <PartialPickupBadge partialPickup={item.partialPickup} compact />
                            <Table.ScrollContainer minWidth={420}>
                                <Table verticalSpacing={4}>
                                    <Table.Tbody>
                                        {item.jobs.map((job) => (
                                            <Table.Tr key={job.id}>
                                                <Table.Td>
                                                    <Anchor
                                                        size="sm"
                                                        renderRoot={(props) => (
                                                            <Link
                                                                {...props}
                                                                to="/jobs/$jobId"
                                                                params={{jobId: job.id}}
                                                            />
                                                        )}
                                                    >
                                                        {job.displayId}
                                                    </Anchor>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text size="sm">
                                                        {job.name}
                                                        {job.isPickup ? ' (pickup)' : ''}
                                                    </Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text size="sm">
                                                        {job.dateTbc
                                                            ? formatDateTbcLabel(null)
                                                            : formatJobDate(job.startDate)}
                                                    </Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Badge
                                                        variant="light"
                                                        size="sm"
                                                        color={jobStatusColor(
                                                            toLifecycleStatus(job.status),
                                                        )}
                                                    >
                                                        {jobStatusLabel(
                                                            toLifecycleStatus(job.status),
                                                        )}
                                                    </Badge>
                                                </Table.Td>
                                            </Table.Tr>
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            </Table.ScrollContainer>
                            {item.equipmentOut.length ? (
                                <Text size="sm">
                                    <Text span fw={600}>
                                        Still out:
                                    </Text>{' '}
                                    {item.equipmentOut.map(formatEquipmentOutLine).join('; ')}
                                </Text>
                            ) : null}
                        </Stack>
                    ))
                )}
            </Stack>
        </Paper>
    )
}
